import React, { useCallback, useMemo } from "react";
import TextTitle from "../common/ui/typography/TextTitle";
import Button from "../common/ui/buttons/Button";
import "./style.scss";
import TextCallout from "../common/ui/typography/TextCallout";
import TextInput from "../common/form/TextInput";
import Checkbox from "../common/form/Checkbox";
import AsyncSelect from "react-select/async";
import { debounce } from "lodash";
import { statistics } from "../../api/statistics";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useGlobalStore } from "../../store";
import { useMutation } from "react-query";
import { useAlert } from "../../hooks/useAlert";
import { referrals } from "../../api/referral";

type FormValues = {
  name: string;
  manager: string;
};

export const ReferralCreate = () => {
  const selectedCompany = useGlobalStore((state) => state.selectedCompany);
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { register, handleSubmit, control, watch } = useForm<FormValues>({
    defaultValues: {
      name: "",
      manager: "",
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: FormValues) =>
      referrals.createByEld(selectedCompany, {
        name: data.name,
        manager: data.manager,
      }),
    onSuccess: (data) => {
      showAlert(
        "success",
        data?.data?.data?.message || "Successfully created!"
      );
      navigate("/referral");
    },
    onError: (err: any) => {
      showAlert(
        "error",
        err?.response?.data?.error.message || "Something went wrong!"
      );
    },
  });

  const onSubmit = (data: FormValues) => {
    createMutation.mutate(data);
  };

  const isValidForm = useMemo(() => {
    const nameFilled = !!watch("name")?.trim();
    const managerFilled = !!watch("manager")?.trim();

    return nameFilled && managerFilled;
  }, [watch("name"), watch("manager")]);

  return (
    <form className="update-create" onSubmit={handleSubmit(onSubmit)}>
      <div className="force-update__header d-flex align-items-center">
        <TextTitle level="large" className="d-flex align-center gap-2">
          New Referral
        </TextTitle>
      </div>
      <div className="update-create__form">
        <div className="d-flex flex-column gap-3">
          <div
            className="d-flex gap-3 w-100"
            style={{
              paddingBottom: "24px",
              borderBottom: "0.5px solid var(--theme-color-grey-2)",
            }}
          >
            <div className="d-flex flex-column gap-2 w-100">
              <TextCallout level="2">Service Name</TextCallout>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextInput placeholder="Enter service name" {...field} />
                )}
              />
            </div>
            <div className="d-flex flex-column gap-2 w-100">
              <TextCallout level="2">Manager Name</TextCallout>
              <Controller
                name="manager"
                control={control}
                render={({ field }) => (
                  <TextInput placeholder="Enter manager name" {...field} />
                )}
              />
            </div>
          </div>
        </div>

        <div className="d-flex gap-3">
          <Button
            className="w-100"
            size="sm"
            to="/referral"
            onClick={() => navigate("/referral")}
            esc
          >
            Cancel
          </Button>
          <Button
            className="w-100"
            size="sm"
            type="submit"
            enter
            main={isValidForm}
            disabled={!isValidForm || createMutation.isLoading}
            loading={createMutation.isLoading}
          >
            Create
          </Button>
        </div>
      </div>
    </form>
  );
};
